/* eslint-disable no-nested-ternary */
import React, { useContext } from 'react';
import { GridOptions } from 'ag-grid-community';
import styled from 'styled-components';
import { useTheme } from '@material-ui/core/styles';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import {
  AgGridTableWrapperOffset,
  Watermark,
  APIAlert,
  Loader,
} from '@mummssoftware/common-ui';
import { hopeKeys, approve } from '@mummssoftware/utils/hope';
import { HopeGlobalContext } from '@mummssoftware/global-context';
import { useIntl } from 'react-intl';
import { Container } from '../../components/layout/WrapperDiv';
import { HopeContext } from '../Context/HopeWidgetContext';
import { Context } from '../../context';
import {
  SET_SHOW_LOADER,
  SET_SHOW_DETAIL,
  SET_HOPE_ERROR,
  SET_SELECTED_ROWS,
} from '../Context/constants';
import { DetailModal } from '../DetailModal';
import { getColumnDefs, commonColumnDefs } from './tableColumnDefs';
import defaults from '../../translations/en.json';

import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';

LicenseManager &&
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);

const BatchMessage = styled.div`
  padding: 3px;
`;

interface HopeProps {
  viewModePrint?: boolean;
  smallWidget?: boolean;
  gridApiRef: any;
}

export const HopeTable = ({
  viewModePrint,
  smallWidget,
  gridApiRef,
}: HopeProps) => {
  const intl = useIntl();
  const { onWidgetReady, watermark, patient } = useContext(Context);
  const {
    getPatientHopeDetails,
    showLoader,
    dispatch,
    hopeError,
    batchMode,
    filteredHopeList,
    selectedRows,
  } = useContext(HopeContext);
  const { globalCrossPatientHopeData, patientHopeDetail } = useContext(
    HopeGlobalContext,
  );
  const {
    props: { grid: gridTheme },
    palette,
  } = useTheme() as any;

  const [
    DetailColumnDefs,
    SummaryColumnDefs,
    SmallSummaryColumnDefs,
  ] = getColumnDefs(palette, batchMode, patient);

  const onCellClicked = (clickEvent: any) => {
    const {
      colDef: { headerClass },
      data,
    } = clickEvent;
    if (headerClass !== 'button') {
      dispatch({ type: SET_SHOW_LOADER, payload: true });
      dispatch({ type: SET_SHOW_DETAIL, payload: data });
      getPatientHopeDetails(data.patientId, data.episodenum);
    }
  };

  const isRowSelectable = (node: any, loading: boolean) => {
    if (loading) return false;
    if (!batchMode) return true;
    const { data } = node;
    return !hopeKeys.find((dtoProp: string) => {
      const {
        numerrors,
        numwarnings,
        reportDownloadAvailable,
        milestone,
      } = data[dtoProp];
      if (
        milestone &&
        ((numerrors !== null && numerrors > 0) ||
          (numwarnings !== null && numwarnings > 0) ||
          (batchMode === approve && !reportDownloadAvailable))
      ) {
        return true;
      }
      return false;
    });
  };

  const columnDefs =
    (smallWidget && SmallSummaryColumnDefs) || SummaryColumnDefs;

  const summaryGridOptions: GridOptions = {
    detailRowAutoHeight: true,
    embedFullWidthRows: true,
    groupHeaderHeight: 20,
    defaultColDef: {
      ...commonColumnDefs,
    },
    columnDefs: [...columnDefs],
    onGridReady: (params: any) => {
      onWidgetReady && onWidgetReady('Hope' as mumms.ComponentName);
      gridApiRef.current = params.api;
    },
  };

  const detailGridOptions: GridOptions = {
    detailRowAutoHeight: true,
    embedFullWidthRows: true,
    groupHeaderHeight: 20,
    defaultColDef: {
      ...commonColumnDefs,
    },
    columnDefs: DetailColumnDefs,
  };

  const clearError = () => {
    dispatch({
      type: SET_HOPE_ERROR,
      payload: null,
    });
  };

  const onSelectionChanged = () => {
    const selected = gridApiRef.current.getSelectedRows();
    dispatch({
      type: SET_SELECTED_ROWS,
      payload: selected.length ? selected : null,
    });
  };
  return (
    <Container id="hope-container" fullHeight={!batchMode}>
      {batchMode && (
        <>
          {!selectedRows?.length ? (
            <BatchMessage>
              {intl.formatMessage(defaults.selectRows, {
                action: batchMode,
              })}
            </BatchMessage>
          ) : (
            ''
          )}
          {selectedRows && selectedRows?.length > 1 ? (
            <BatchMessage>
              {intl.formatMessage(defaults.rowCounts, {
                action: batchMode.charAt(0).toUpperCase() + batchMode.slice(1),
                number: selectedRows?.length || 0,
              })}
            </BatchMessage>
          ) : selectedRows?.length === 1 ? (
            <BatchMessage>
              {intl.formatMessage(defaults.rowCount, {
                action: batchMode.charAt(0).toUpperCase() + batchMode.slice(1),
                number: selectedRows?.length || 0,
              })}
            </BatchMessage>
          ) : (
            ''
          )}
          <BatchMessage>
            {batchMode === 'approve' ? defaults.approvalStatement : ''}
          </BatchMessage>
        </>
      )}
      {showLoader && <Loader />}
      {watermark && watermark.show ? (
        <Watermark
          opacity=".2"
          readOnly={watermark.readOnly}
          smallWidget={smallWidget}
        />
      ) : null}
      {hopeError && (
        <APIAlert
          errorInfo={{ text: hopeError || '', severity: 'error' }}
          handleClose={() => clearError()}
        />
      )}
      <DetailModal />
      <AgGridTableWrapperOffset
        id="hope-table-wrapper"
        className={`${gridTheme.className} light max-width`}
        printMode={viewModePrint}
        smallWidget={smallWidget}
      >
        {/* temporary for cross patient widget to show on preview */}
        {(!patient && (
          <AgGridReact
            key={`${JSON.stringify(columnDefs)}${showLoader}`}
            onSelectionChanged={onSelectionChanged}
            gridOptions={summaryGridOptions}
            rowData={
              (batchMode ? filteredHopeList : globalCrossPatientHopeData) ??
              undefined
            }
            rowHeight={gridTheme.rowHeight}
            headerHeight={gridTheme.headerHeight}
            onCellClicked={(!batchMode && onCellClicked) || undefined}
            rowSelection={(batchMode && 'multiple') || 'single'} // or "multiple"
            suppressCellSelection={!!batchMode} // IMPORTANT
            suppressRowClickSelection={!batchMode}
            rowMultiSelectWithClick={!!batchMode}
            suppressPropertyNamesCheck
            isRowSelectable={(node: any) => isRowSelectable(node, showLoader)}
          />
        )) || (
          <AgGridReact
            gridOptions={detailGridOptions}
            rowData={patientHopeDetail as any}
            rowHeight={gridTheme.rowHeight}
            headerHeight={gridTheme.headerHeight}
            suppressRowClickSelection
            suppressCellSelection
            suppressPropertyNamesCheck
          />
        )}
      </AgGridTableWrapperOffset>
    </Container>
  );
};

export default HopeTable;
